import { requestAvatarSelection } from "../utils/avatar-selection-events";
import React, { Component, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import copy from "copy-to-clipboard";
import { FormattedMessage } from "react-intl";
import screenfull from "screenfull";
import configs from "../utils/configs";
import {
  canEnterFromRoomSession,
  canWaitInLobbyFromRoomSession,
  subscriptionsEnabled
} from "../utils/rtc-sfu-room-session";
import { normalizeRoomEntryFailure, ROOM_ENTRY_FAILURE_KIND } from "./room/room-entry-failure-utils";
import { VR_DEVICE_AVAILABILITY } from "../utils/vr-caps-detect";
import { canShare } from "../utils/share";
import { withIdleDetectionSuspendedUntilForeground } from "../utils/idle-detection-suspender";
import styles from "../assets/stylesheets/ui-root.scss";
import { ReactAudioContext } from "./wrap-with-audio";
import {
  pushHistoryState,
  clearHistoryState,
  popToBeginningOfHubHistory,
  navigateToPriorPage,
  sluglessPath
} from "../utils/history";
import StateRoute from "./state-route.js";
import { getPresenceProfileForSession, hubUrl } from "../utils/phoenix-utils";
import { getMicrophonePresences } from "../utils/microphone-presence";
import { getCurrentStreamer } from "../utils/component-utils";
import { isIOS } from "../utils/is-mobile";

import ProfileEntryPanel from "./profile-entry-panel";

import EntryStartPanel from "./entry-start-panel.js";
import PreferencesScreen from "./preferences-screen.js";
import { PresenceLog } from "./presence-log.js";
import PreloadOverlay from "./preload-overlay.js";
import RTCDebugPanel from "./debug-panel/RtcDebugPanel.js";
import { showFullScreenIfAvailable, showFullScreenIfWasFullScreen } from "../utils/fullscreen";
import { handleExitTo2DInterstitial, exit2DInterstitialAndEnterVR, isIn2DInterstitial } from "../utils/vr-interstitial";
import { remountUI } from "../hub";
import maskEmail from "../utils/mask-email";

import qsTruthy from "../utils/qs_truthy";
import { LoadingScreenContainer } from "./room/LoadingScreenContainer";

import { RoomLayoutContainer } from "./room/RoomLayoutContainer";
import roomLayoutStyles from "./layout/RoomLayout.scss";
import { useAccessibleOutlineStyle } from "./input/useAccessibleOutlineStyle";
import { RoomEntryModal } from "./room/RoomEntryModal";
import { EnterOnDevice, EnterOnDeviceModal } from "./room/EnterOnDeviceModal";
import { MicSetupModalContainer } from "./room/MicSetupModalContainer";
import { InvitePopoverContainer } from "./room/InvitePopoverContainer";
import { MoreMenuContextProvider } from "./room/MoreMenuPopover";
import { ProfileMenuContextProvider } from "./room/ProfileMenu";
import { userFromPresence } from "./room/PeopleSidebarContainer";
import { ObjectListProvider } from "./room/useObjectList";
import { ObjectsSidebarContainer } from "./room/ObjectsSidebarContainer";
import { ObjectMenuContainer } from "./room/ObjectMenuContainer";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { SafariMicModal } from "./room/SafariMicModal";
import { RoomSignInModalContainer } from "./auth/RoomSignInModalContainer";
import { AuthPopupRetryDialog } from "./auth/AuthPopupRetryDialog";
import { SignInStep } from "./auth/SignInModal";
import { LeaveReason, LeaveRoomModal } from "./room/LeaveRoomModal";
import { RoomSidebar } from "./room/RoomSidebar";
import { AutoExitWarningModal, AutoExitReason } from "./room/AutoExitWarningModal";
import { shouldSuppressIdleAutoExit } from "./room/idle-auto-exit";
import { ExitReason } from "./room/ExitedRoomScreen";
import { UserProfileSidebarContainer } from "./room/UserProfileSidebarContainer";
import { WebVRUnsupportedModal } from "./room/WebVRUnsupportedModal";
import { TweetModalContainer } from "./room/TweetModalContainer";
import { TipContainer, FullscreenTip } from "./room/TipContainer";
import { SpectatingLabel } from "./room/SpectatingLabel";
import { SignInMessages } from "./auth/SignInModal";
import { MediaDevicesEvents } from "../utils/media-devices-utils";
import { FirstPersonAvatarPreview } from "./room/FirstPersonAvatarPreview";
import { PortalsSidebarContainer } from "./room/PortalsSidebarContainer";
import { OpenLinkModal } from "./room/OpenLinkModal";
import { ECSDebugSidebarContainer } from "./debug-panel/ECSSidebar";
import { NotificationsContainer } from "./room/NotificationsContainer";
import { usePermissions } from "./room/usePermissions";
import { LoadingDots } from "./room/LoadingDots";
import { IframeLinkModal } from "./room/IframeLinkModal";
import { Modal2DDialog } from "./room/Modal2DDialog";
import { Component2DView } from "./room/Component2DView";
import { ToastMessage } from "./room/ToastMessage";
import { ToastDialog } from "./room/ToastDialog";
import { ObjectURLMediaModalContainer } from "./room/ObjectURLMediaModalContainer";
import { EventContextProvider, useEvent } from "./room/EventContextProvider";
import { simulateKeyPress } from "../utils/custom-utils";
import Cookies from "js-cookie";
import { StreamTextFallback } from "./room/StreamViewFailModal";
import { PrivateChatContextProvider } from "./chat/PrivateChatContext";
import { StorageLimitModal } from "./room/StorageLimitModal";
import { MediaAcquisitionErrorModal } from "./room/MediaAcquisitionErrorModal";
import { ObjectUrlUploadModalContainer } from "./room/ObjectUrlUploadModalContainer";
import { ObjectNotPermissionModal } from "./room/ObjectNotPermissionModal";
import { ProgressDialog } from "./modal/ProgressDialog";
import configManager from "../singleton/ConfigManager";
import configManagerEmitter from "../utils/configManagerEmitter";
import {
  getReticulumRoomEntryAdmission,
  getRoomEntryAuthority,
  isWebRtcDisabled,
  requiresDialogConnection,
  shouldShowEntryAudioSetup,
  RETICULUM_ADMISSION_STATUS,
  ROOM_ENTRY_AUTHORITY
} from "../utils/room-entry-admission";
import { ButtonInfo } from "./button-info";

import { ChatContextProvider } from "./hooks/useChat";
import { RoomPersistenceProvider } from "./room-persistence/RoomPersistenceContext";
import { NotificationsCenterToaster } from "./room/NotificationsCenterToaster";
import { SettingsProvider } from "./room/SettingsContext";
import RoomSettingsSidebarContainer from "./layout/FluentRoomSettingsSidebarContainer";
import { ModalContextProvider } from "./room/ModalContext";
import { SidebarContextProvider } from "./SidebarContext";
import { StateContextProvider } from "./room/StateContext";
import notificationsCenter from "../singleton/NotificationsCenter";
import { LogMessageType } from "../utils/messages";
import { AssetsSidebarContainer } from "./room/AssetsSidebarContainer";
import { ObjectInspectSidebarContainer } from "./room/ObjectInspectSidebarContainer";
import { RoomAssetLoadingProvider, useRoomAssetLoading } from "./room/RoomAssetLoadingTracker";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DropArea from "./layout/DropArea";
import { ObjectSettingsProvider } from "./contexts/ObjectSettingsProvider";
import { ArtworkInfoSidebarContainer } from "./room/ArtworkInfoSidebarContainer";
import { UploadFormProvider } from "./contexts/UploadFormContext";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { CookieConsentModalContainer } from "./CookieConsentModalConainer";
import { UPLOAD_DRAWER_TOGGLER_ID } from "./contexts/ToolbarContext";
import { CameraDevicesProvider } from "./room/useCameraDevices";
import aiAgents from "../singleton/AIAgents";
import { RoomEntryErrorModal } from "./room/RoomEntryErrorModal";
import { EnterOnVRPopover } from "./room/EnterOnVRPopover";
import { PortalsButton } from "./room/PortalsButton";
import { FloatyButtonsContainer } from "./room/FloatyButtonsContainer";
import { Video2DContainer } from "./room/Video2DContainer";
import { AvatarEditor as FluentAvatarEditor } from "./room/AvatarEditor";
import AvatarEditor from "./avatar-editor";
import { AvatarPersistenceManager } from "./room/AvatarPersistenceManager";
import { ScheduleSidebar } from "./room/ScheduleSidebar";
import {
  clearDashboardBridgeSessionState,
  clearDashboardBridgeToken,
  getDashboardBridgeToken,
  loginWithDashboardBridgePopup,
  logoutWithDashboardBridge,
  renewDashboardBridgeToken
} from "../utils/dashboard-bridge";
import { closeAuthPopupFromError, getAuthPopupRetryReason } from "../utils/auth-popup-policy";
import { completeMetaverseLoginAfterBridge } from "../utils/metaverse-auth-bootstrap";
import { INSTANT_ACCESS_AUTH_EXPIRED_EVENT, isInstantAccessMode } from "../utils/getToken";
import { captureReauthHintFromSession } from "../utils/reauth-hints";
import { completeOrStartLocalBridgeRehydration } from "../utils/local-bridge-rehydration";
import {
  buildDeviceLinkHandoffPayload,
  encryptDeviceLinkPayload,
  getDeviceLinkRoomPath
} from "../utils/device-link-crypto";
import { formatDeviceLinkCode } from "../utils/device-link-code";
import { isDeviceLinkGrantInactive } from "../utils/device-link-grant-status";
import { getGateLinkUrl } from "../utils/gate-link-url";

const avatarEditorDebug = qsTruthy("avatarEditorDebug");

// EventReadinessWrapper to bridge EventProvider state with UIRoot
const EventReadinessWrapper = ({ children, onEventReady, eventSlug }) => {
  const { isLoading, error, isInTheEvent } = useEvent();

  useEffect(() => {
    // If there's no event slug, mark as ready immediately
    if (!eventSlug) {
      onEventReady(true);
      return;
    }

    // If there's an event slug, wait for both:
    // 1. Event data loading to complete (!isLoading)
    // 2. Event participation check to complete (isInTheEvent !== null)
    if (!isLoading && isInTheEvent !== null) {
      onEventReady(true);
    }
  }, [isLoading, isInTheEvent, eventSlug, onEventReady]);

  return children;
};

const IN_ROOM_MODAL_ROUTER_PATHS = ["/media"];
const IN_ROOM_MODAL_QUERY_VARS = ["media_source"];

const LOBBY_MODAL_ROUTER_PATHS = ["/media/scenes", "/media/avatars", "/media/favorites"];
const LOBBY_MODAL_QUERY_VARS = ["media_source"];
const LOBBY_MODAL_QUERY_VALUES = ["scenes", "avatars", "favorites"];

async function grantedMicLabels() {
  const mediaDevices = await navigator.mediaDevices.enumerateDevices();
  return mediaDevices.filter(d => d.label && d.kind === "audioinput").map(d => d.label);
}

const isSmallScreen = window.innerWidth < 450;
const isMobile = AFRAME.utils.device.isMobile();
const isMobileVR = AFRAME.utils.device.isMobileVR();
const AUTO_EXIT_TIMER_SECONDS = 10;
const AUTH0_TOKEN_STORAGE_KEY = "auth0token";
const AUTH0_TOKEN_COOKIE_KEY = "auth0token";
const AUTH_MODE_KEY = "auth_mode";
const INSTANT_ACCESS_SID_KEY = "instant_access_sid";
const INSTANT_ACCESS_USERNAME_KEY = "instant_access_username";

function clearAuth0Token() {
  captureReauthHintFromSession(localStorage);
  localStorage.removeItem(AUTH0_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_MODE_KEY);
  localStorage.removeItem(INSTANT_ACCESS_SID_KEY);
  localStorage.removeItem(INSTANT_ACCESS_USERNAME_KEY);
  clearDashboardBridgeToken();
  clearDashboardBridgeSessionState();
  Cookies.remove(AUTH0_TOKEN_COOKIE_KEY, { domain: configs.AUTH0_COOKIE_DOMAIN });
  // Keep a fallback clear without domain for environments where domain isn't applied.
  Cookies.remove(AUTH0_TOKEN_COOKIE_KEY);
}

class UIRoot extends Component {
  willCompileAndUploadMaterials = false;
  activeDeviceLink = null;
  cancelDeviceLinkRequested = false;
  reticulumEntryInProgress = false;

  static propTypes = {
    enterScene: PropTypes.func,
    enterSceneInVR: PropTypes.func,
    connectToDialog: PropTypes.func,
    exitScene: PropTypes.func,
    onSendMessage: PropTypes.func,
    disableAutoExitOnIdle: PropTypes.bool,
    forcedVREntryType: PropTypes.string,
    isBotMode: PropTypes.bool,
    store: PropTypes.object,
    mediaSearchStore: PropTypes.object,
    scene: PropTypes.object,
    authChannel: PropTypes.object,
    hubChannel: PropTypes.object,
    dashboardChannel: PropTypes.object,
    avatarSelectionController: PropTypes.object,
    hub: PropTypes.object,
    availableVREntryTypes: PropTypes.object,
    checkingForDeviceAvailability: PropTypes.bool,
    environmentSceneLoaded: PropTypes.bool,
    entryDisallowed: PropTypes.bool,
    roomUnavailableReason: PropTypes.string,
    rtcSfuConnectionState: PropTypes.string,
    dialogConnected: PropTypes.bool,
    hasDialogConnectedOnce: PropTypes.bool,
    hubIsBound: PropTypes.bool,
    isSupportAvailable: PropTypes.bool,
    presenceLogEntries: PropTypes.array,
    presences: PropTypes.object,
    sessionId: PropTypes.string,
    subscriptions: PropTypes.object,
    initialIsFavorited: PropTypes.bool,
    showSignInDialog: PropTypes.bool,
    signInMessage: PropTypes.object,
    onContinueAfterSignIn: PropTypes.func,
    onCancel: PropTypes.func,
    showSafariMicDialog: PropTypes.bool,
    onMediaSearchResultEntrySelected: PropTypes.func,
    onAvatarSaved: PropTypes.func,
    location: PropTypes.object,
    history: PropTypes.object,
    showInterstitialPrompt: PropTypes.bool,
    onInterstitialPromptClicked: PropTypes.func,
    performConditionalSignIn: PropTypes.func,
    hide: PropTypes.bool,
    showPreload: PropTypes.bool,
    onPreloadLoadClicked: PropTypes.func,
    embed: PropTypes.bool,
    onLoaded: PropTypes.func,
    activeObject: PropTypes.object,
    selectedObject: PropTypes.object,
    breakpoint: PropTypes.string,
    canVoiceChat: PropTypes.bool,
    isLoading: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    onInternalLoadingStateChange: PropTypes.func
  };

  state = {
    enterInVR: false,
    enterInAR: false,
    entered: false,
    entering: false,
    enteringError: false,
    dialog: null,
    showShareDialog: false,
    linkCode: null,
    linkCodeCancel: null,
    linkCodeSourceSecret: null,
    loadingLinkCode: false,
    miniInviteActivated: false,
    showEnterOnDevicePopover: false,

    didConnectToNetworkedScene: false,
    noMoreLoadingUpdates: false,
    hideLoader: false,
    showPrefs: false,
    watching: false,
    isStreaming: false,
    isRecordingMode: false,

    waitingOnAudio: false,
    joiningVerse: false,
    audioTrackClone: null,

    autoExitTimerStartedAt: null,
    autoExitTimerInterval: null,
    autoExitReason: null,
    secondsRemainingBeforeAutoExit: Infinity,

    signedIn: false,
    videoShareMediaSource: null,
    showVideoShareFailed: false,

    objectInfo: null,
    objectSrc: "",
    sidebarId: null,
    presenceCount: 0,
    // showToolbar: !isMobile,
    showToolbar: true,
    chatPrefix: "",
    chatAutofocus: false,

    showingMyMedia: false,
    toast: {
      dialog: null,
      component: null,
      toastProps: null
    },
    iframe2D: {
      dialog: false,
      src: null
    },
    canEnterRoom: undefined,
    roomSession: null,
    roomEntryFailure: null,
    isEventDataReady: false
  };

  constructor(props) {
    super(props);

    props.mediaSearchStore.setHistory(props.history);

    // An exit handler that discards event arguments and can be cleaned up.
    this.exitEventHandler = () => this.props.exitScene(ExitReason.backgrounded);
    this.mediaDevicesManager = APP.mediaDevicesManager;
    this.showIframe2D = this.showIframe2D.bind(this);
    this.showModal2D = this.showModal2D.bind(this);
    this.showMediaModal = this.showMediaModal.bind(this);
    this.showToastMessage = this.showToastMessage.bind(this);
    this.closeiframe2D = this.closeiframe2D.bind(this);
    this.showStreamFailModal = this.showStreamFailModal.bind(this);
    this.showNotPermissionModal = this.showNotPermissionModal.bind(this);
    this.setSidebar = this.setSidebar.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { hubChannel, showSignInDialog } = this.props;

    if (hubChannel) {
      const { signedIn } = hubChannel;
      if (signedIn !== this.state.signedIn) {
        this.setState({ signedIn });
      }
    }

    if (prevProps.showSignInDialog !== showSignInDialog) {
      if (showSignInDialog) {
        this.showContextualSignInDialog();
      } else {
        this.closeDialog();
      }
    }

    if (!this.willCompileAndUploadMaterials && this.state.noMoreLoadingUpdates) {
      this.willCompileAndUploadMaterials = true;
      // We want to ensure that react and the browser have had the chance to render / update.
      // See https://stackoverflow.com/a/34999925 , although our solution flipped setTimeout and requestAnimationFrame
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          if (!this.props.isBotMode) {
            try {
              this.props.scene.renderer.compile(this.props.scene.object3D, this.props.scene.camera);
              this.props.scene.object3D.traverse(obj => {
                if (!obj.material) {
                  return;
                }
                const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
                for (const material of materials) {
                  for (const prop in material) {
                    if (material[prop] && material[prop].isTexture) {
                      this.props.scene.renderer.initTexture(material[prop]);
                    }
                  }
                }
              });
            } catch (e) {
              console.error(e);
              this.props.exitScene(ExitReason.sceneError); // https://github.com/mozilla/hubs/issues/1950
            }
          }

          if (!this.state.hideLoader) {
            this.setState({ hideLoader: true });
          }
        }, 0);
      });
    }

    if (!this.props.selectedObject || !prevProps.selectedObject) {
      const sceneEl = this.props.scene;

      if (this.props.selectedObject) {
        sceneEl.classList.add(roomLayoutStyles.sceneSmFullScreen);
        // this.setState({ showToolbar: false });
      } else {
        sceneEl.classList.remove(roomLayoutStyles.sceneSmFullScreen);
      }
    }

    if (this.state.presenceCount != this.occupantCount()) {
      this.setState({ presenceCount: this.occupantCount() });
    }

    if (!this.props.isLoading && prevProps.isLoading !== this.props.isLoading) {
      this.signInUserIfHasToken();
    }

    const authBootstrapChannelReady =
      (!prevProps.authChannel && this.props.authChannel) || (!prevProps.hubChannel && this.props.hubChannel);
    if (authBootstrapChannelReady) {
      this.signInUserIfHasToken();
    }

    const dashboardChannelReady =
      !prevProps.dashboardChannel?.requestRtcSfuRoomSession && this.props.dashboardChannel?.requestRtcSfuRoomSession;
    if (
      configManager.synced &&
      this.props.hub &&
      (!prevProps.hub || prevProps.hub.hub_id !== this.props.hub.hub_id || dashboardChannelReady)
    ) {
      this.checkIfUserCanEnterRoom(this.props.hub.hub_id).then(canEnter => {
        this.setState({ canEnterRoom: canEnter });
      });
    }

    if (this.props.onInternalLoadingStateChange) {
      const wasLoading = this._getIsMyLoadingScreenActive(prevProps, prevState);
      const isLoading = this._getIsMyLoadingScreenActive(this.props, this.state);
      if (isLoading !== wasLoading) {
        this.props.onInternalLoadingStateChange(isLoading);
      }
    }
  }

  onIframeActivity = event => {
    const events = ["click", "pointerdown", "touchstart", "keydown"];
    if (
      event.origin === configs["PLACE_ART_MARKETPLACE"] &&
      typeof event.data == "object" &&
      events.includes(event.data.eventType)
    ) {
      window.dispatchEvent(new CustomEvent("resetIdleTimeout"));
    }
  };

  shouldAllowConcurrentSessions = () => {
    return qsTruthy("allow_multi") || this.props.store.state.preferences.allowMultipleHubsInstances;
  };

  onConcurrentLoad = () => {
    if (this.shouldAllowConcurrentSessions()) return;
    this.startAutoExitTimer(AutoExitReason.concurrentSession);
  };

  handleDeviceLinkHandoffConfirmed = async () => {
    this.cancelDeviceLinkRequested = false;

    if (this.shouldAllowConcurrentSessions()) {
      return;
    }

    this.props.exitScene(ExitReason.deviceLinkedSession);
  };

  onIdleDetected = () => {
    if (
      shouldSuppressIdleAutoExit({
        disableAutoExitOnIdle: this.props.disableAutoExitOnIdle,
        disableIdleDetection: this.props.store.state.preferences.disableIdleDetection,
        isStreaming: this.state.isStreaming,
        isWebcamShared: this.mediaDevicesManager.isWebcamShared,
        isScreenShared: this.mediaDevicesManager.isScreenShared
      })
    )
      return;
    this.startAutoExitTimer(AutoExitReason.idle);
  };

  onActivityDetected = () => {
    if (this.state.autoExitTimerInterval) {
      this.endAutoExitTimer();
    }
  };

  exitReasonForAutoExitReason = autoExitReason => {
    if (autoExitReason === AutoExitReason.idle) {
      return ExitReason.idle;
    }

    if (autoExitReason === AutoExitReason.concurrentSession) {
      return ExitReason.concurrentSession;
    }

    return undefined;
  };

  _isSigningIn = false;
  _isPopupSignInInProgress = false;

  hasLocalReticulumSession = () =>
    Boolean(this.state.signedIn || this.props.hubChannel?.signedIn || this.props.store.state.credentials?.token);

  clearLocalReticulumSessionIfBridgeMissing = async () => {
    if (this.hasLocalReticulumSession() && !isInstantAccessMode()) {
      await this.signOut(false, false, true);
    }
  };

  signInUserIfHasToken = async () => {
    // Prevent concurrent calls
    if (this._isSigningIn) {
      return;
    }

    this._isSigningIn = true;
    let didEmitFetchingAuthUser = false;

    try {
      let token = getDashboardBridgeToken() || (await renewDashboardBridgeToken().catch(() => null));

      if (!token) {
        const rehydration = await completeOrStartLocalBridgeRehydration().catch(() => ({ status: "skipped" }));
        if (rehydration.status === "redirecting") {
          return;
        }

        token = getDashboardBridgeToken();
      }

      if (!token) {
        await this.clearLocalReticulumSessionIfBridgeMissing();
        return;
      }

      if (!this.props.authChannel || !this.props.hubChannel) {
        return;
      }

      this.props.scene.emit("fetching_auth_user");
      didEmitFetchingAuthUser = true;

      const result = await completeMetaverseLoginAfterBridge({
        store: this.props.store,
        authChannel: this.props.authChannel,
        hubChannel: this.props.hubChannel,
        dashboardToken: token
      });

      if (result.status === "duplicate") {
        const user = result.user;
        this.showNonHistoriedDialog(RoomSignInModalContainer, {
          step: SignInStep.duplicate,
          onClose: () => {
            this.signOut(false);
            this.closeDialog();
          },
          onContinue: () => {
            this.signOut(false, true);
            this.closeDialog();
          },
          email: user.email,
          provider: user.auth0_provider
        });
        return;
      }

      if (result.status === "signed_in") {
        this.setState({ signedIn: true });

        if (this.props.onContinueAfterSignIn) {
          this.props.onContinueAfterSignIn();
        }

        this.closeDialog();
        return;
      }

      await this.clearLocalReticulumSessionIfBridgeMissing();
    } finally {
      if (didEmitFetchingAuthUser) {
        this.props.scene.emit("fetching_auth_user_end");
      }
      this._isSigningIn = false;
    }
  };

  showSettingsIfQueryExist = () => {
    const interval = setInterval(() => {
      if (!this.props.hubChannel) {
        return;
      }

      clearInterval(interval);

      if (qsTruthy("room_settings")) {
        this.setSidebar("room-settings");
      }
    }, 500);
  };

  showNotPermissionModal() {
    this.showNonHistoriedDialog(ObjectNotPermissionModal, { onClose: this.closeDialog });
  }

  showModal2D(event) {
    this.showNonHistoriedDialog(Modal2DDialog, { el: event.detail });
  }

  showMediaModal(event) {
    const scene = this.props.scene;
    this.showNonHistoriedDialog(ObjectURLMediaModalContainer, {
      entry: event.detail,
      scene: scene,
      onClose: this.closeDialog
    });
  }

  showIframe2D(event) {
    this.setState({
      iframe2D: {
        dialog: true,
        src: event.detail
      }
    });
  }

  closeiframe2D() {
    this.setState({
      iframe2D: {
        dialog: false
      }
    });
  }

  showToastMessage(event) {
    const message = event.detail;
    this.setState({
      toast: {
        dialog: true,
        Component: ToastMessage,
        toastProps: {
          message,
          timeout: 4000,
          onClose: () =>
            this.setState({
              toast: {
                dialog: false,
                Component: null
              }
            })
        }
      }
    });
  }

  onInstantAccessAuthExpired = event => {
    const message =
      event?.detail?.message || "Your Instant Access session has ended. Open your Instant Access link to continue.";

    notificationsCenter.warning("Instant Access Session Ended", message, {
      timeout: 8000
    });
  };

  showStreamFailModal() {
    this.showNonHistoriedDialog(StreamTextFallback, {
      onClose: this.closeDialog
    });
  }

  _getIsMyLoadingScreenActive(props, state) {
    const preload = props.showPreload;
    const internalIsLoading = !preload && !state.hideLoader;
    const eventDataNotReady = !state.isEventDataReady;

    if (props.showSafariMicDialog) return false;
    if (state.isRecordingMode) return false;
    if (props.hide || state.hide) return false;

    if (internalIsLoading && state.showPrefs) return true;

    if (props.isBotMode) return false;

    if (internalIsLoading || eventDataNotReady) return true;

    return false;
  }

  componentDidMount() {
    configManagerEmitter.on("configUpdated", this.onConfigUpdated);
    window.addEventListener("message", this.onIframeActivity);
    window.addEventListener("concurrentload", this.onConcurrentLoad);
    window.addEventListener("idle_detected", this.onIdleDetected);
    window.addEventListener("activity_detected", this.onActivityDetected);
    window.addEventListener("focus_chat", this.onFocusChat);
    window.addEventListener("open-2D-iframe", this.showIframe2D);
    window.addEventListener("open-2D-modal", this.showModal2D);
    // window.addEventListener("open-media-modal", this.showMediaModal);
    window.addEventListener("not-permission-modal", this.showNotPermissionModal);
    window.addEventListener("toast-message", this.showToastMessage);
    window.addEventListener(INSTANT_ACCESS_AUTH_EXPIRED_EVENT, this.onInstantAccessAuthExpired);
    // window.addEventListener("stream-fail", this.showStreamFailModal);
    document.querySelector(".a-canvas").addEventListener("mouseup", () => {
      if (this.state.showShareDialog) {
        this.setState({ showShareDialog: false });
      }
    });

    this.props.scene.addEventListener("loaded", this.onSceneLoaded);
    this.props.scene.addEventListener("share_video_enabled", this.onShareVideoEnabled);
    this.props.scene.addEventListener("share_video_disabled", this.onShareVideoDisabled);
    this.props.scene.addEventListener("share_video_failed", this.onShareVideoFailed);
    this.props.scene.addEventListener("exit", this.exitEventHandler);
    this.props.scene.addEventListener("action_exit_watch", () => {
      if (this.state.hide) {
        this.setState({ hide: false, hideUITip: false });
      } else {
        this.setState({ watching: false, sidebarId: null });
      }
    });
    this.props.scene.addEventListener("action_toggle_ui", () =>
      this.setState({ hide: !this.state.hide, hideUITip: false })
    );
    this.props.scene.addEventListener("action_toggle_record", () => {
      const cursor = document.querySelector("#right-cursor");
      if (this.state.isRecordingMode) {
        if (cursor.object3D.children[0]?.material) {
          cursor.object3D.children[0].material.visible = true;
        }
        this.setState({ hide: false, hideUITip: false, isRecordingMode: false });
        document.querySelector(".rs-fps-counter").style.visibility = "visible";
        document.querySelector(".rs-base").style.visibility = "visible";
      } else {
        if (cursor.object3D.children[0]?.material) {
          cursor.object3D.children[0].material.visible = false;
        }
        notificationsCenter.info(LogMessageType.recordModeEnabled);
        this.setState({ hide: true, hideUITip: true, isRecordingMode: true });
        document.querySelector(".rs-fps-counter").style.visibility = "hidden";
        document.querySelector(".rs-base").style.visibility = "hidden";
      }
    });
    this.props.scene.addEventListener("devicechange", () => {
      this.forceUpdate();
    });

    const scene = this.props.scene;

    scene.addEventListener("open-upload-form", () => {
      this.setSidebar(UPLOAD_DRAWER_TOGGLER_ID);
    });

    const unsubscribe = this.props.history.listen((location, action) => {
      const state = location.state;

      // If we just hit back into the entry flow, just go back to the page before the room landing page.
      if (action === "POP" && state && state.entry_step && this.state.entered) {
        unsubscribe();
        navigateToPriorPage(this.props.history);
        return;
      }
    });

    // If we refreshed the page with any state history (eg if we were in the entry flow
    // or had a modal/overlay open) just reset everything to the beginning of the flow by
    // erasing all history that was accumulated for this room (including across refreshes.)
    //
    // We don't do this for the media browser case, since we want to be able to share
    // links to the browser pages
    if (this.props.history.location.state && !sluglessPath(this.props.history.location).startsWith("/media")) {
      popToBeginningOfHubHistory(this.props.history);
    }

    this.setState({
      audioContext: {
        playSound: sound => {
          scene.emit(sound);
        },
        onMouseLeave: () => {
          //          scene.emit("play_sound-hud_mouse_leave");
        }
      }
    });

    if (this.props.forcedVREntryType && this.props.forcedVREntryType.endsWith("_now")) {
      this.props.scene.addEventListener(
        "loading_finished",
        () => {
          setTimeout(() => this.handleForceEntry(), 1000);
        },
        { once: true }
      );
    }

    this.playerRig = scene.querySelector("#avatar-rig");

    scene.addEventListener("action_media_nft_mint", this.onMintNft);
    scene.addEventListener("action_media_tweet", this.onTweet);

    this.showSettingsIfQueryExist();

    this.props.scene.addEventListener("open_link_failed", e => {
      const url = e.detail.url;

      this.showNonHistoriedDialog(OpenLinkModal, {
        destinationUrl: url,
        reason:
          "Your browser is blocking popups from this site. Please click the button below to open the link in a new tab."
      });
    });

    this.props.scene.addEventListener("storage_limit_reached", this.onStorageLimitReached);
    this.props.scene.addEventListener("media_acquisition_started", this.onMediaAcquisitionStarted);
    this.props.scene.addEventListener("media_acquisition_finished", this.onMediaAcquisitionFinished);
    this.props.scene.addEventListener("media_acquisition_failed", this.onMediaAcquisitionFailed);

    this.props.scene.addEventListener("action_spawn", async () => {
      await handleExitTo2DInterstitial(false, () => this.setSidebar(null));
      this.setSidebar("assets");
    });

    this.props.scene.addEventListener("action_invite", async () => {
      await handleExitTo2DInterstitial(false, () => this.setSidebar(null));
      this.setSidebar("invite");
    });

    this.props.scene.addEventListener("action_participants", async () => {
      await handleExitTo2DInterstitial(false, () => this.setSidebar(null));
      this.setSidebar("participants");
    });

    this.onConfigUpdated();

    if (this.props.onInternalLoadingStateChange) {
      this.props.onInternalLoadingStateChange(this._getIsMyLoadingScreenActive(this.props, this.state));
    }

    this.signInUserIfHasToken();
  }

  UNSAFE_componentWillMount() {
    this.props.store.addEventListener("statechanged", this.storeUpdated);
  }

  componentWillUnmount() {
    configManagerEmitter.off("configUpdated", this.onConfigUpdated);
    this.props.scene.removeEventListener("loaded", this.onSceneLoaded);
    this.props.scene.removeEventListener("exit", this.exitEventHandler);
    this.props.scene.removeEventListener("share_video_enabled", this.onShareVideoEnabled);
    this.props.scene.removeEventListener("share_video_disabled", this.onShareVideoDisabled);
    this.props.scene.removeEventListener("share_video_failed", this.onShareVideoFailed);
    this.props.scene.removeEventListener("action_media_tweet", this.onTweet);
    this.props.scene.removeEventListener("action_media_nft_mint", this.onMintNft);
    this.props.scene.removeEventListener("media_acquisition_started", this.onMediaAcquisitionStarted);
    this.props.scene.removeEventListener("media_acquisition_finished", this.onMediaAcquisitionFinished);
    this.props.scene.removeEventListener("media_acquisition_failed", this.onMediaAcquisitionFailed);
    this.props.store.removeEventListener("statechanged", this.storeUpdated);
    window.removeEventListener("not-permission-modal", this.showNotPermissionModal);
    this.props.scene.removeEventListener("open-upload-form", () => {
      this.setSidebar(UPLOAD_DRAWER_TOGGLER_ID);
    });
    this.props.scene.removeEventListener("action_spawn", () => {
      handleExitTo2DInterstitial(false, () => this.setSidebar(null));
      this.setSidebar("assets");
    });
    this.props.scene.removeEventListener("action_invite", async () => {
      await handleExitTo2DInterstitial(false, () => this.setSidebar(null));
      this.setSidebar("invite");
    });
    this.props.scene.removeEventListener("action_participants", async () => {
      await handleExitTo2DInterstitial(false, () => this.setSidebar());
      this.setSidebar("participants");
    });
    window.removeEventListener("concurrentload", this.onConcurrentLoad);
    window.removeEventListener("idle_detected", this.onIdleDetected);
    window.removeEventListener("activity_detected", this.onActivityDetected);
    window.removeEventListener("focus_chat", this.onFocusChat);
    window.removeEventListener(INSTANT_ACCESS_AUTH_EXPIRED_EVENT, this.onInstantAccessAuthExpired);
    // window.removeEventListener("stream-fail", this.showStreamFailModal);
  }

  storeUpdated = () => {
    this.forceUpdate();
  };

  onConfigUpdated = () => {
    if (!configManager.synced || !this.props.hub) return;

    this.checkIfUserCanEnterRoom(this.props.hub.hub_id).then(canEnter => {
      this.setState({ canEnterRoom: canEnter });
    });
  };

  showPopupRetryDialog = reason =>
    new Promise(resolve => {
      const closeAndResolve = shouldRetry => {
        this.closeDialog();
        resolve(shouldRetry);
      };

      this.showNonHistoriedDialog(AuthPopupRetryDialog, {
        reason,
        onRetry: () => closeAndResolve(true),
        onCancel: () => closeAndResolve(false),
        onClose: () => closeAndResolve(false)
      });
    });

  bridgePopupRetryHandling = async () => {
    while (true) {
      try {
        await loginWithDashboardBridgePopup(window.location.href);
        return true;
      } catch (error) {
        const retryReason = getAuthPopupRetryReason(error, configs.AUTH0_POPUP_TIMEOUT_SECONDS);

        if (!retryReason) {
          return false;
        }

        closeAuthPopupFromError(error);

        const shouldRetry = await this.showPopupRetryDialog(retryReason);
        if (!shouldRetry) {
          return false;
        }
      }
    }
  };

  showContextualSignInDialog = async () => {
    if (this._isPopupSignInInProgress) {
      return;
    }

    this._isPopupSignInInProgress = true;

    const { onContinueAfterSignIn } = this.props;

    try {
      const didSignIn = await this.bridgePopupRetryHandling();
      if (didSignIn) {
        await this.signInUserIfHasToken();
      }
    } finally {
      this._isPopupSignInInProgress = false;
      if (onContinueAfterSignIn) {
        onContinueAfterSignIn();
      }
      showFullScreenIfWasFullScreen();
    }
  };

  updateSubscribedState = () => {
    const isSubscribed = this.props.subscriptions && this.props.subscriptions.isSubscribed();
    this.setState({ isSubscribed });
  };

  toggleFavorited = () => {
    this.props.performConditionalSignIn(
      () => this.props.hubChannel.signedIn,
      () => {
        const isFavorited = this.isFavorited();

        this.props.hubChannel[isFavorited ? "unfavorite" : "favorite"]();
        this.setState({ isFavorited: !isFavorited });
      },
      SignInMessages.favoriteRoom
    );
  };

  isFavorited = () => {
    return this.state.isFavorited !== undefined ? this.state.isFavorited : this.props.initialIsFavorited;
  };

  onLoadingFinished = () => {
    this.setState({ noMoreLoadingUpdates: true });
    this.props.scene.emit("loading_finished");

    if (this.props.onLoaded) {
      this.props.onLoaded();
    }

    // Tried to get the camera before hand
    // commented by issues
    // if (configManager.config.roomEntryDialog.enterOnAR.enabled) {
    //   navigator.permissions
    //     .query({ name: "camera" })
    //     .then(function (permissionStatus) {
    //       console.log("permission Status", permissionStatus);
    //       if (permissionStatus.state === "denied") {
    //         alert(
    //           "Your browser is currently blocking camera access. To enable it, please go to your browser settings and allow camera permissions for this site."
    //         );
    //         // Handle blocked camera access here
    //       } else if (permissionStatus.state === "granted") {
    //         // console.log("Camera access is allowed.");
    //         // Camera is available for use
    //       } else if (permissionStatus.state === "prompt") {
    //         // console.log("Camera access has not been requested yet.");
    //         // // The browser will ask for permission when needed
    //       }
    //     })
    //     .catch(function (error) {
    //       console.error("Error querying permissions:", error);
    //     });
    // }
  };

  onSceneLoaded = () => {
    this.setState({ sceneLoaded: true });
  };

  onShareVideoEnabled = e => {
    this.setState({ videoShareMediaSource: e.detail.source });
  };

  onShareVideoDisabled = () => {
    this.setState({ videoShareMediaSource: null });
  };

  onShareVideoFailed = () => {
    this.setState({ showVideoShareFailed: true });
  };

  shareVideo = mediaSource => {
    this.props.scene.emit(`action_share_${mediaSource}`);
  };

  endShareVideo = () => {
    this.props.scene.emit(MediaDevicesEvents.VIDEO_SHARE_ENDED);
  };

  spawnPen = () => {
    this.props.scene.emit("penButtonPressed");
  };

  onSubscribeChanged = async () => {
    if (!this.props.subscriptions) return;

    await this.props.subscriptions.toggle();
    this.updateSubscribedState();
  };

  handleForceEntry = () => {
    if (!this.props.forcedVREntryType) return;

    if (this.props.forcedVREntryType.startsWith("daydream")) {
      this.enterDaydream();
    } else if (this.props.forcedVREntryType.startsWith("vr")) {
      this.enterVR();
    } else if (this.props.forcedVREntryType.startsWith("2d")) {
      this.enter2D();
    }
  };

  handleAIAudio = () => {
    // let aiEntities = getAiEntities();
    // if (aiEntities.length > 0) {
    //   aiEntities[0]?.components["npc-avatar"]?.playBlankAudio();
    // }
    aiAgents.playBlankAudioAll();
  };

  startAutoExitTimer = autoExitReason => {
    if (this.state.autoExitTimerInterval) return;

    const autoExitTimerInterval = setInterval(() => {
      let secondsRemainingBeforeAutoExit = Infinity;

      if (this.state.autoExitTimerStartedAt) {
        const secondsSinceStart = (new Date() - this.state.autoExitTimerStartedAt) / 1000;
        secondsRemainingBeforeAutoExit = Math.max(0, Math.floor(AUTO_EXIT_TIMER_SECONDS - secondsSinceStart));
      }

      this.setState({ secondsRemainingBeforeAutoExit });
      this.checkForAutoExit();
    }, 500);

    this.setState({ autoExitTimerStartedAt: new Date(), autoExitTimerInterval, autoExitReason });
  };

  checkForAutoExit = () => {
    if (this.state.secondsRemainingBeforeAutoExit !== 0) return;
    const exitReason = this.exitReasonForAutoExitReason(this.state.autoExitReason);
    this.endAutoExitTimer();
    this.props.exitScene(exitReason);
  };

  isWaitingForAutoExit = () => {
    return this.state.secondsRemainingBeforeAutoExit <= AUTO_EXIT_TIMER_SECONDS;
  };

  endAutoExitTimer = () => {
    clearInterval(this.state.autoExitTimerInterval);
    this.setState({
      autoExitTimerStartedAt: null,
      autoExitTimerInterval: null,
      autoExitReason: null,
      secondsRemainingBeforeAutoExit: Infinity
    });
  };

  performDirectEntryFlow = async enterInVR => {
    this.setState({ enterInVR, waitingOnAudio: true });

    const hasGrantedMic = (await grantedMicLabels()).length > 0;

    if (hasGrantedMic) {
      await this.mediaDevicesManager.fetchMediaDevices();
      this.beginOrSkipAudioSetup();
    } else {
      this.pushHistoryState("entry_step", "audio");
      await this.onRequestMicPermission();
    }

    this.setState({ waitingOnAudio: false });
  };

  enter2D = async () => {
    await this.performDirectEntryFlow(false);
  };

  enterVR = async () => {
    if (this.props.forcedVREntryType || this.props.availableVREntryTypes.generic !== VR_DEVICE_AVAILABILITY.maybe) {
      await this.performDirectEntryFlow(true);
    } else {
      this.showNonHistoriedDialog(WebVRUnsupportedModal);
    }
  };

  enterDaydream = async () => {
    await this.performDirectEntryFlow(true);
  };

  onRequestMicPermission = async () => {
    if (this.props.canVoiceChat) {
      await (isMobile
        ? withIdleDetectionSuspendedUntilForeground(
            () => this.mediaDevicesManager.ensureMicPermissionAndEnumerate(),
            "room-entry-mic-permission"
          )
        : this.mediaDevicesManager.ensureMicPermissionAndEnumerate());
    }
  };

  beginOrSkipAudioSetup = () => {
    const skipAudioSetup = this.props.forcedVREntryType && this.props.forcedVREntryType.endsWith("_now");
    if (skipAudioSetup) {
      this.onAudioReadyButton();
    } else {
      this.pushHistoryState("entry_step", "audio");
    }
  };

  shouldShowFullScreen = () => {
    // Disable full screen on iOS, since Safari's fullscreen mode does not let you prevent native pinch-to-zoom gestures.
    return (isMobile || AFRAME.utils.device.isMobileVR()) && !isIOS() && !this.state.enterInVR && screenfull.enabled;
  };

  requestRoomSessionForEntry = async () => {
    if (!subscriptionsEnabled(configs)) {
      return { state: "legacy_active", canEnter: true, peerMode: "active" };
    }

    const roomId = this.props.hub?.hub_id;
    const dashboardChannel = this.props.dashboardChannel || window.APP?.dashboardChannel;
    const requestRoomSession = dashboardChannel?.requestRtcSfuRoomSession;

    if (!roomId || !requestRoomSession) {
      return null;
    }

    const roomSession = await requestRoomSession({
      roomId,
      clientId: this.props.sessionId,
      reserve: true,
      roomSessionId: this.state.roomSession?.roomSessionId,
      reconnectToken: this.state.roomSession?.reconnectToken
    });

    this.setState({
      roomSession,
      canEnterRoom: canEnterFromRoomSession(roomSession)
    });

    return roomSession;
  };

  connectToDialogForRoomSession = async roomSession => {
    const connectToDialog = this.props.connectToDialog || window.APP?.connectToDialog;

    if (connectToDialog) {
      await connectToDialog(roomSession);
    }
  };

  enterSpectateMode = () => {
    this.setState({ watching: true, entering: false });
    notificationsCenter.info(LogMessageType.lobbyTip, null, {
      timeout: 5000
    });
    setTimeout(async () => {
      this.props.scene.systems["hubs-systems"].characterController.shouldLandWhenPossible = false;
      this.props.scene.systems["hubs-systems"].characterController.fly = false;
      await new Promise(resolve => setTimeout(resolve, 100));
      simulateKeyPress("w");
    }, 100);
  };

  closeRoomEntryError = () => {
    this.setState({ enteringError: false });
  };

  retryRoomEntry = () => {
    this.setState({ enteringError: false }, this.onAudioReadyButton);
  };

  handleRoomEntryFailure = (source, { showDialog } = {}) => {
    const roomEntryFailure = normalizeRoomEntryFailure(source, { config: configManager.config });
    const shouldShowDialog = showDialog ?? roomEntryFailure.showDialog !== false;
    const nextState = {
      entering: false,
      roomEntryFailure,
      enteringError: shouldShowDialog,
      showShareDialog: false,
      waitingOnAudio: false,
      joiningVerse: false
    };

    if (roomEntryFailure.kind === ROOM_ENTRY_FAILURE_KIND.CAPACITY_FULL) {
      clearHistoryState(this.props.history);
      nextState.canEnterRoom = false;
      nextState.enterInVR = false;
      nextState.enterInAR = false;
    }

    this.setState(nextState);

    return roomEntryFailure;
  };

  handleUnavailableRoomSession = async roomSession => {
    this.handleRoomEntryFailure(roomSession, { showDialog: true });
  };

  onAudioReadyButton = async () => {
    if (this.state.joiningVerse) return false;

    this.setState({ joiningVerse: true });

    try {
      const hasWebXR = this.props.scene.hasWebXR;
      const supportsAR = hasWebXR && AFRAME.utils.device.checkARSupport();
      const enterInWebXrAR = supportsAR && this.state.enterInAR;
      const enterInFallbackAR = this.state.enterInAR && !supportsAR;
      const enterInVR = this.state.enterInVR && !enterInFallbackAR;

      if (!this.state.enterInVR && !this.state.enterInAR) {
        await showFullScreenIfAvailable();
      }

      if (enterInFallbackAR) {
        await this.props.scene.systems["ar-camera"]?.initCamera();
      }

      const roomEntryAuthority = getRoomEntryAuthority(configManager.config);
      let roomSession = null;

      if (roomEntryAuthority === ROOM_ENTRY_AUTHORITY.RETICULUM) {
        const admission = getReticulumRoomEntryAdmission({ hub: this.props.hub, hubChannel: this.props.hubChannel });
        if (admission !== RETICULUM_ADMISSION_STATUS.AVAILABLE) {
          this.props.hubChannel.sendEnteringCancelledEvent();
          this.handleRoomEntryFailure({
            state: "denied",
            canEnter: false,
            reason: admission === RETICULUM_ADMISSION_STATUS.CAPACITY_FULL ? "active_capacity_full" : "unavailable"
          });
          return false;
        }
        this.props.hubChannel.sendEnteringEvent();
        this.reticulumEntryInProgress = true;
      } else {
        roomSession = await this.requestRoomSessionForEntry();

        if (!roomSession || !canEnterFromRoomSession(roomSession)) {
          await this.handleUnavailableRoomSession(roomSession);
          return false;
        }

        await this.connectToDialogForRoomSession(roomSession);
      }

      const muteOnEntry =
        roomEntryAuthority === ROOM_ENTRY_AUTHORITY.RETICULUM ||
        this.props.store.state.preferences.muteMicOnEntry ||
        !configManager.config.voiceChat.enabled;
      if (roomEntryAuthority === ROOM_ENTRY_AUTHORITY.DIALOG && muteOnEntry && this.props.canVoiceChat) {
        this.mediaDevicesManager.prepareMutedMicShare().catch(error => {
          console.warn("Unable to prepare muted microphone capture before entry.", error);
        });
      }

      await this.props.enterScene(enterInVR, muteOnEntry, enterInWebXrAR);

      // Push the new history state before going into VR, otherwise menu button will take us back
      clearHistoryState(this.props.history);
      this.setState({
        entered: true,
        entering: false,
        joiningVerse: false,
        showShareDialog: false,
        roomEntryFailure: null
      });
      this.reticulumEntryInProgress = false;

      if (enterInFallbackAR) {
        await this.props.scene.systems["ar-camera"]?.connectOrientation();
        await this.props.scene.systems["ar-camera"]?.cameraStart();
      }

      if (isMobile && enterInFallbackAR) {
        this.props.scene.systems["ar-camera"]?.orientation?.addEventListener(
          "change",
          () => {
            AFRAME.scenes[0].object3D.entered = true;
          },
          { once: true }
        );
      } else {
        AFRAME.scenes[0].object3D.entered = true;
      }

      this.props.scene.systems["ar-camera"]?.capture?.forceRendererResize();

      if (enterInFallbackAR) {
        await showFullScreenIfAvailable();
      }

      if (qsTruthy("edit_avatar")) {
        this.setSidebar("avatar");
      }

      return true;
    } catch (e) {
      console.error(e);
      if (this.reticulumEntryInProgress) {
        this.props.hubChannel.sendEnteringCancelledEvent();
        this.reticulumEntryInProgress = false;
      }
      clearHistoryState(this.props.history);
      this.setState({ entered: false, joiningVerse: false });
      this.handleRoomEntryFailure(e);
      return false;
    }
  };

  onEnterOnDevice = async () => {
    this.pushHistoryState("entry_step", "device");
    this.attemptLink();
  };

  cancelActiveDeviceLink = () => {
    this.cancelDeviceLinkRequested = true;
    const active = this.activeDeviceLink;

    if (active?.grantId) {
      active.dashboardChannel?.cancelDeviceLink?.(active.grantId, { sourceSecret: active.sourceSecret });
    }

    this.activeDeviceLink = null;
    this.setState({ linkCode: null, linkCodeCancel: null, linkCodeSourceSecret: null, loadingLinkCode: false });
  };

  attemptLink = async () => {
    const dashboardChannel = this.props.dashboardChannel || window.APP?.dashboardChannel;
    if (!dashboardChannel?.createDeviceLink) {
      return;
    }

    this.activeDeviceLink = null;
    this.cancelDeviceLinkRequested = false;
    this.setState({ loadingLinkCode: true, linkCodeSourceSecret: null, linkCodeCancel: null });

    try {
      const roomPath = getDeviceLinkRoomPath();
      const deviceLink = await dashboardChannel?.createDeviceLink({ roomPath });
      if (!deviceLink?.grant_id || !deviceLink?.user_code) {
        this.setState({ linkCode: null, linkCodeCancel: null, linkCodeSourceSecret: null, loadingLinkCode: false });
        return;
      }

      const sourceSecret = deviceLink.source_secret || null;
      const displayCode = formatDeviceLinkCode(deviceLink.user_code);
      const activeDeviceLink = {
        dashboardChannel,
        grantId: deviceLink.grant_id,
        sourceSecret
      };

      if (this.cancelDeviceLinkRequested) {
        dashboardChannel?.cancelDeviceLink?.(deviceLink.grant_id, { sourceSecret });
        this.activeDeviceLink = null;
        this.setState({ linkCode: null, linkCodeCancel: null, linkCodeSourceSecret: null, loadingLinkCode: false });
        return;
      }

      this.activeDeviceLink = activeDeviceLink;

      await new Promise(resolve => {
        this.setState(
          {
            linkCode: displayCode,
            linkCodeSourceSecret: sourceSecret,
            linkCodeCancel: this.cancelActiveDeviceLink,
            loadingLinkCode: false
          },
          resolve
        );
      });

      while (this.activeDeviceLink === activeDeviceLink) {
        const grant = await dashboardChannel?.getDeviceLink(deviceLink.grant_id, {
          sourceSecret: activeDeviceLink.sourceSecret
        });
        const claim = grant?.claim;

        if (claim?.claim_id && claim?.target_encryption_public_jwk) {
          const handoffPayload = buildDeviceLinkHandoffPayload(this.props.store, roomPath);
          const encryptedHandoffPayload = await encryptDeviceLinkPayload(claim.target_encryption_public_jwk, handoffPayload);
          await dashboardChannel?.confirmDeviceLink({
            grantId: deviceLink.grant_id,
            claimId: claim.claim_id,
            encryptedHandoffPayload,
            sourceSecret: activeDeviceLink.sourceSecret
          });
          this.activeDeviceLink = null;
          this.cancelDeviceLinkRequested = false;
          this.setState({
            log: false,
            linkCode: null,
            linkCodeCancel: null,
            linkCodeSourceSecret: null,
            loadingLinkCode: false
          });
          await this.handleDeviceLinkHandoffConfirmed();
          return;
        }

        if (isDeviceLinkGrantInactive(grant)) {
          this.activeDeviceLink = null;
          this.setState({
            linkCode: null,
            linkCodeCancel: null,
            linkCodeSourceSecret: null,
            loadingLinkCode: false
          });
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    } catch (error) {
      this.activeDeviceLink = null;
      this.setState({ linkCode: null, linkCodeCancel: null, linkCodeSourceSecret: null, loadingLinkCode: false });
      throw error;
    }
  };

  toggleShareDialog = async () => {
    this.props.store.update({ activity: { hasOpenedShare: true } });
    this.setState({ showShareDialog: !this.state.showShareDialog });
  };

  closeDialog = () => {
    if (this.state.dialog) {
      this.setState({ dialog: null });
    }

    if (isIn2DInterstitial()) {
      exit2DInterstitialAndEnterVR();
    } else {
      showFullScreenIfWasFullScreen();
    }
  };

  showNonHistoriedDialog = (DialogClass, props = {}) => {
    this.setState({
      dialog: <DialogClass {...{ onClose: this.closeDialog, ...props }} />
    });
  };

  toggleStreamerMode = () => {
    const isStreaming = !this.state.isStreaming;
    this.props.scene.systems["hubs-systems"].characterController.fly = isStreaming;

    if (isStreaming) {
      notificationsCenter.info(LogMessageType.lobbyCast);
      this.props.hubChannel.beginStreaming();
    } else {
      this.props.hubChannel.endStreaming();
    }

    this.setState({ isStreaming });
  };

  renderDialog = (DialogClass, props = {}) => <DialogClass {...{ onClose: this.closeDialog, ...props }} />;

  signOut = (iframeLogout = true, reSignIn = false, localOnly = false) =>
    new Promise(resolve => {
      const interval = setInterval(async () => {
        void iframeLogout;

        if (!this.props.authChannel || !this.props.hubChannel) {
          return;
        }

        clearInterval(interval);

        const shouldLogoutFromBridge = (this.props.isAuthenticated || this.props.store.state.authUser) && !localOnly;

        let redirectUrl = window.location.href;

        await this.props.authChannel.signOut(this.props.hubChannel, { resetAvatar: !localOnly });

        this.setState({ signedIn: false });
        this.props.store.update({ authUser: {}, credentials: { token: null, email: null } });

        if (reSignIn) {
          if (redirectUrl.includes("?")) {
            redirectUrl += "&signin=true";
          } else {
            redirectUrl += "?signin=true";
          }
        }

        clearAuth0Token();

        if (shouldLogoutFromBridge) {
          localStorage.setItem("redirectAfterLoginUrl", redirectUrl);
          logoutWithDashboardBridge(redirectUrl);
        }

        resolve();
      }, 1000);
    });

  onMiniInviteClicked = async () => {
    const dashboardChannel = this.props.dashboardChannel || window.APP?.dashboardChannel;

    this.setState({ miniInviteActivated: true });
    setTimeout(() => {
      this.setState({ miniInviteActivated: false });
    }, 5000);

    try {
      const link = await dashboardChannel?.shareHub?.(this.props.hub.hub_id);
      if (!link) {
        throw new Error("Room share link is unavailable");
      }

      if (canShare()) {
        navigator.share({ title: document.title, url: link });
      } else {
        copy(link);
      }
    } catch (error) {
      console.error("Failed to generate room share shortlink", error);
      notificationsCenter.error("Verse link is currently unavailable");
    }
  };

  sendMessage = msg => {
    this.props.onSendMessage(msg);
  };

  occupantCount = () => {
    return this.props.presences ? Object.entries(this.props.presences).length : 0;
  };

  hasEmbedPresence = () => {
    if (!this.props.presences) {
      return false;
    } else {
      for (const p of Object.values(this.props.presences)) {
        for (const m of p.metas) {
          if (m.context && m.context.embed) {
            return true;
          }
        }
      }
    }

    return false;
  };

  onTweet = ({ detail }) => {
    handleExitTo2DInterstitial(true, () => {}).then(() => {
      this.props.performConditionalSignIn(
        () => this.props.hubChannel.signedIn,
        () => {
          this.showNonHistoriedDialog(TweetModalContainer, {
            hubChannel: this.props.hubChannel,
            isAdmin: configs.isAdmin(),
            ...detail
          });
        },
        SignInMessages.tweet
      );
    });
  };

  onMintNft = () => {
    handleExitTo2DInterstitial(true, () => {}).then(() => {
      this.props.performConditionalSignIn(
        () => this.props.hubChannel.signedIn,
        this.showContextualSignInDialog,
        SignInMessages.mintNft
      );
    });
  };

  onChangeScene = () => {
    this.setState({ showPrefs: false });
    this.props.performConditionalSignIn(
      () => this.props.hubChannel.can("update_hub"),
      () => {
        showFullScreenIfAvailable();
        this.props.mediaSearchStore.sourceNavigateWithNoNav("scenes", "use");
      },
      SignInMessages.changeScene
    );
  };

  pushHistoryState = (k, v) => pushHistoryState(this.props.history, k, v);

  setSidebar(sidebarId, otherState) {
    this.setState({ sidebarId, chatPrefix: "", chatAutofoucs: false, selectedUserId: null, ...otherState });

    if (sidebarId === "avatar") {
      if (window.APP && window.APP.setMainSceneRenderSuppressed) {
        window.APP.setMainSceneRenderSuppressed(true, "avatar-sidebar");
      }
    } else if (this.state.sidebarId === "avatar") {
      if (window.APP && window.APP.setMainSceneRenderSuppressed) {
        window.APP.setMainSceneRenderSuppressed(false, "avatar-sidebar");
      }
    }
  }

  onEventReady = isReady => {
    if (isReady && !this.state.isEventDataReady) {
      this.setState({ isEventDataReady: true });
    }
  };

  toggleSidebar(sidebarId, otherState) {
    this.setState(({ sidebarId: curSidebarId }) => {
      const nextSidebarId = curSidebarId === sidebarId ? null : sidebarId;

      return {
        sidebarId: nextSidebarId,
        selectedUserId: null,
        ...otherState
      };
    });
  }

  onFocusChat = e => {
    this.setSidebar("chat", {
      chatPrefix: e.detail.prefix,
      chatAutofocus: true
    });
  };

  renderInterstitialPrompt = () => {
    return (
      <div className={styles.interstitial} onClick={() => this.props.onInterstitialPromptClicked()}>
        <div>
          <FormattedMessage id="ui-root.interstitial-prompt" defaultMessage="Continue" />
        </div>
      </div>
    );
  };

  renderBotMode = () => {
    return (
      <div className="loading-panel">
        <img className="loading-panel__logo" src={configs.image("logo")} />
        <input type="file" id="bot-audio-input" accept="audio/*" />
        <input type="file" id="bot-data-input" accept="application/json" />
      </div>
    );
  };

  onEnteringCanceled = () => {
    this.props.hubChannel.sendEnteringCancelledEvent();
    this.reticulumEntryInProgress = false;
    this.setState({ entering: false });
  };

  onStorageLimitReached = event => {
    this.showNonHistoriedDialog(StorageLimitModal, {
      onClose: this.closeDialog,
      storageLimitDetail: event?.detail || null
    });
  };

  onMediaAcquisitionStarted = () => {
    this.showNonHistoriedDialog(ProgressDialog, {
      title: "Adding media",
      message: "Saving link to workspace...",
      cancelable: false
    });
  };

  onMediaAcquisitionFinished = () => {
    this.closeDialog();
  };

  onMediaAcquisitionFailed = event => {
    this.closeDialog();
    this.showNonHistoriedDialog(MediaAcquisitionErrorModal, {
      message: event?.detail?.message
    });
  };

  renderEntryStartPanel = () => {
    const { hasAcceptedProfile, hasChangedName } = this.props.store.state.activity;
    //const isLockedDownDemo = configs.feature("is_locked_down_demo_room") === this.props.hub.hub_id;
    const isLockedDownDemo = false;
    const promptForNameAndAvatarBeforeEntry = this.props.hubIsBound
      ? !hasAcceptedProfile && !isLockedDownDemo
      : !hasChangedName && !isLockedDownDemo;

    if (this.state.sidebarId === "room-settings") return <></>;

    const canLookAround =
      this.state.canEnterRoom === false
        ? canWaitInLobbyFromRoomSession(
            this.state.roomSession || { state: "denied", canEnter: false },
            configManager.config
          )
        : true;

    // TODO: What does onEnteringCanceled do?
    return (
      <>
        <RoomEntryModal
          roomId={this.props.hub.hub_id}
          roomName={this.props.hub.name}
          roomDescription={this.props.hub.description}
          roomUserData={this.props.hub ? this.props.hub.user_data : null}
          showJoinRoom={!this.state.waitingOnAudio && !this.props.entryDisallowed}
          onJoinAR={() => {
            this.setState({ enterInAR: true, enterInVR: true });
          }}
          showEnterOnAR={this.props.scene.renderType === "AR"}
          onJoinRoom={() => {
            const roomEntryAuthority = getRoomEntryAuthority(configManager.config);
            if (isLockedDownDemo) {
              if (this.props.forcedVREntryType?.startsWith("vr")) {
                this.setState({ enterInVR: true }, this.onAudioReadyButton);
                return;
              }
              return this.onAudioReadyButton();
            }
            if (promptForNameAndAvatarBeforeEntry || !this.props.forcedVREntryType) {
              this.setState({ entering: true, enteringError: false, roomEntryFailure: null });
              if (roomEntryAuthority === ROOM_ENTRY_AUTHORITY.DIALOG) {
                this.props.hubChannel.sendEnteringEvent();
              }

              const showAvatarStep =
                configManager.config.avatarDialog.enabled &&
                (configManager.config?.avatarDialog?.showName ||
                  configManager.config?.avatarDialog?.showAvatar ||
                  configManager.config?.avatarDialog?.showPreview);

              const showAudioVideoStep = requiresDialogConnection(configManager.config);

              if (promptForNameAndAvatarBeforeEntry && showAvatarStep) {
                this.pushHistoryState("entry_step", "profile");
              } else if (showAudioVideoStep) {
                this.onRequestMicPermission();
                this.pushHistoryState("entry_step", "audio");
              } else {
                this.onAudioReadyButton();
              }
            } else {
              this.handleForceEntry();
            }
            this.handleAIAudio();
          }}
          canEnter={this.state.canEnterRoom}
          canLookAround={canLookAround}
          isJoiningVerse={this.state.joiningVerse}
          showEnterOnDevice={!this.state.waitingOnAudio && !this.props.entryDisallowed && !isMobileVR}
          onEnterOnDevice={() => this.onEnterOnDevice()}
          showSpectate={!this.state.waitingOnAudio}
          onSpectate={async () => {
            if (subscriptionsEnabled(configs) && this.state.canEnterRoom === false) {
              const roomSession = this.state.roomSession || { state: "denied", canEnter: false };

              if (!canWaitInLobbyFromRoomSession(roomSession, configManager.config)) {
                this.setState({ canEnterRoom: false });
                return;
              }
            }

            this.enterSpectateMode();
          }}
          onOpenSchedule={() => this.setSidebar("schedule")}
          onRoomSettings={() => {
            this.props.performConditionalSignIn(
              () => this.props.hubChannel.can("update_hub"),
              () => this.setSidebar("room-settings"),
              SignInMessages.roomSettings
            );
          }}
        />
        {!this.state.waitingOnAudio && (
          <EntryStartPanel
            hubChannel={this.props.hubChannel}
            entering={this.state.entering}
            onEnteringCanceled={this.onEnteringCanceled}
          />
        )}
        <RoomEntryErrorModal
          open={this.state.enteringError}
          failure={this.state.roomEntryFailure}
          onClose={this.closeRoomEntryError}
          onLookAround={() => {
            this.closeRoomEntryError();
            this.enterSpectateMode();
          }}
          onRetry={this.retryRoomEntry}
        />
      </>
    );
  };

  renderDevicePanel = () => {
    return (
      <EnterOnDeviceModal
        shortUrl={getGateLinkUrl()}
        loadingCode={this.state.loadingLinkCode}
        code={this.state.linkCode}
        headsetConnected={AFRAME.utils.device.checkHeadsetConnected()}
        attemptLink={this.attemptLink}
        unsupportedBrowser={this.props.availableVREntryTypes.generic === VR_DEVICE_AVAILABILITY.maybe}
        onEnterOnConnectedHeadset={() => {
          this.enterVR();
          this.cancelActiveDeviceLink();
        }}
        onBack={() => {
          this.cancelActiveDeviceLink();
          this.setState({
            enterInVR: false,
            enterInAR: false
          });
          this.props.history.goBack();
        }}
      />
    );
  };

  renderAudioSetupPanel = () => {
    // TODO: Show HMD mic not chosen warning
    return (
      <MicSetupModalContainer
        scene={this.props.scene}
        enteredInAr={this.state.enterInAR}
        isJoiningVerse={this.state.joiningVerse}
        onEnterRoom={this.onAudioReadyButton}
        onBack={() => {
          if (this.state.enterInAR) {
            this.setState({ enterInAR: false, enterInVR: false });
          }
          this.props.history.goBack();
        }}
      />
    );
  };

  isInModalOrOverlay = () => {
    if (this.sidebarId === "avatar") {
      return true;
    }

    if (
      this.state.entered &&
      (IN_ROOM_MODAL_ROUTER_PATHS.find(x => sluglessPath(this.props.history.location).startsWith(x)) ||
        IN_ROOM_MODAL_QUERY_VARS.find(x => new URLSearchParams(this.props.history.location.search).get(x)) ||
        this.state.iframe2D.dialog)
    ) {
      return true;
    }

    if (
      !this.state.entered &&
      (LOBBY_MODAL_ROUTER_PATHS.find(x => sluglessPath(this.props.history.location).startsWith(x)) ||
        LOBBY_MODAL_QUERY_VARS.find(
          (x, i) => new URLSearchParams(this.props.history.location.search).get(x) === LOBBY_MODAL_QUERY_VALUES[i]
        ))
    ) {
      return true;
    }

    if (this.state.objectInfo && this.state.objectInfo.object3D) {
      return true; // TODO: Get object info dialog to use history
    }
    if (this.state.sidebarId !== null) {
      return true;
    }

    if (this.state.showingMyMedia) {
      return true;
    }

    return !!(
      (this.props.history &&
        this.props.history.location.state &&
        (this.props.history.location.state.modal || this.props.history.location.state.overlay)) ||
      this.state.dialog
    );
  };

  getSelectedUser() {
    const selectedUserId = this.state.selectedUserId;
    const presence = this.props.presences[selectedUserId];
    const micPresences = getMicrophonePresences();
    return userFromPresence(selectedUserId, presence, micPresences, this.props.sessionId);
  }

  checkIfUserCanEnterRoom = async roomId => {
    if (!configManager.synced) return undefined;

    if (getRoomEntryAuthority(configManager.config) === ROOM_ENTRY_AUTHORITY.RETICULUM) {
      return (
        getReticulumRoomEntryAdmission({ hub: this.props.hub, hubChannel: this.props.hubChannel }) ===
        RETICULUM_ADMISSION_STATUS.AVAILABLE
      );
    }

    if (!subscriptionsEnabled(configs)) {
      return true;
    }

    const dashboardChannel = this.props.dashboardChannel || window.APP?.dashboardChannel;
    if (!roomId || !dashboardChannel?.requestRtcSfuRoomSession) {
      return true;
    }

    try {
      const roomSession = await dashboardChannel.requestRtcSfuRoomSession({
        roomId,
        clientId: this.props.sessionId,
        reserve: false
      });

      APP.rtcSfuRoomSession = roomSession;
      this.setState({ roomSession });

      return canEnterFromRoomSession(roomSession);
    } catch (error) {
      console.error("Error checking if user can enter room:", error);
      this.handleRoomEntryFailure(error);
      return true;
    }
  };

  render() {
    const isNotAbsolute = this.state.sidebarId === "objects" ? true : false;

    const hide = this.state.hide || this.props.hide;
    const isGhost = configs.feature("enable_lobby_ghosts") && (this.state.watching || hide);

    // REMOVE: This cause the cursor to be hidden for all modals and not going back
    // this.isInModalOrOverlay()
    //   ? this.props.scene.addState("in-modal-or-overlay")
    //   : this.props.scene.removeState("in-modal-or-overlay");

    const rootStyles = {
      [styles.ui]: true,
      "ui-root": true,
      "in-modal-or-overlay": this.isInModalOrOverlay(),
      isGhost,
      hide
    };
    const preload = this.props.showPreload;
    const uiRootInternalIsLoading = !preload && !this.state.hideLoader;

    const entered = this.state.entered;
    const watching = this.state.watching;
    const enteredOrWatching = entered || watching;
    const hasDashboardAuthUser = Boolean(
      this.props.store.state.authUser?.id && this.props.store.state.authUser?.profile?.id
    );
    const isDashboardSignedIn = this.state.signedIn && hasDashboardAuthUser;

    if (this.props.showSafariMicDialog) {
      return (
        <StateContextProvider
          entered={this.state.entered}
          watching={this.state.watching}
          onEnteredChange={value => this.setState({ entered: value })}
          onWatchingChange={value => this.setState({ watching: value, ...(!value && { sidebarId: null }) })}
        >
          <div className={classNames(rootStyles)}>
            <RoomLayoutContainer
              scene={this.props.scene}
              store={this.props.store}
              isNotAbsolute={isNotAbsolute}
              modal={<SafariMicModal />}
              showToolbar={false}
              renderEntryFlow={renderEntryFlow}
            />
          </div>
        </StateContextProvider>
      );
    }

    if (this.state.isRecordingMode) {
      return (
        <div className={classNames(rootStyles)}>
          <RoomLayoutContainer scene={this.props.scene} showToolbar={false} store={this.props.store} viewport={<></>} />
        </div>
      );
    }

    if (this.props.hide || this.state.hide) {
      return (
        <div className={classNames(rootStyles)}>
          <RoomLayoutContainer
            hubChannel={this.props.hubChannel}
            scene={this.props.scene}
            store={this.props.store}
            isNotAbsolute={isNotAbsolute}
            viewport={
              !this.state.hideUITip &&
              configManager.config.UI.enabled && <FullscreenTip onDismiss={() => this.setState({ hideUITip: true })} />
            }
            showToolbar={false}
          />
        </div>
      );
    }

    if (uiRootInternalIsLoading && this.state.showPrefs) {
      return (
        <div>
          <LoadingScreenContainer scene={this.props.scene} hub={this.props.hub} onLoaded={this.onLoadingFinished} />
          <PreferencesScreen
            onClose={() => {
              this.setState({ showPrefs: false });
            }}
            roomSettings={
              <RoomSettingsSidebarContainer
                className="room-settings-sidebar"
                hub={this.props.hub}
                isSettings={true}
                accountId={this.props.sessionId}
                hubChannel={this.props.hubChannel}
                dashboardChannel={this.props.dashboardChannel}
                roomSession={this.state.roomSession}
                onChangeScene={this.onChangeScene}
                onClose={() => {
                  this.setState({ showPrefs: false });
                  this.setSidebar(null);
                }}
                onSubmit={() => {
                  this.setState({ showPrefs: false });
                  this.setSidebar(null);
                }}
              />
            }
            store={this.props.store}
            scene={this.props.scene}
          />
        </div>
      );
    }
    if (this.props.isBotMode) return this.renderBotMode();
    if (uiRootInternalIsLoading || this.state.canEnterRoom === undefined) {
      return <LoadingScreenContainer scene={this.props.scene} hub={this.props.hub} onLoaded={this.onLoadingFinished} />;
    }
    if (this.state.showPrefs) {
      return (
        <PreferencesScreen
          onClose={() => {
            this.setState({ showPrefs: false });
          }}
          canEditScene={this.props.hubChannel.canOrWillIfCreator("update_hub")}
          roomSettings={
            <RoomSettingsSidebarContainer
              className="room-settings-sidebar"
              hub={this.props.hub}
              isSettings={true}
              accountId={this.props.sessionId}
              hubChannel={this.props.hubChannel}
              dashboardChannel={this.props.dashboardChannel}
              roomSession={this.state.roomSession}
              onChangeScene={this.onChangeScene}
              onClose={() => {
                this.setState({ showPrefs: false });
                this.setSidebar(null);
              }}
              onSubmit={() => {
                this.setState({ showPrefs: false });
                this.setSidebar(null);
              }}
            />
          }
          store={this.props.store}
          scene={this.props.scene}
        />
      );
    }

    if (this.props.showInterstitialPrompt) return this.renderInterstitialPrompt();

    const showRtcDebugPanel = this.props.store.state.preferences.showRtcDebugPanel;
    const showAudioDebugPanel = this.props.store.state.preferences.showAudioDebugPanel;
    const displayNameOverride = this.props.hubIsBound
      ? getPresenceProfileForSession(this.props.presences, this.props.sessionId).displayName
      : null;

    // TODO: Should we re-add this?
    const enableSpectateVRButton =
      configs.feature("enable_lobby_ghosts") &&
      isGhost &&
      !hide &&
      this.props.availableVREntryTypes.generic !== VR_DEVICE_AVAILABILITY.no;

    const entryDialog =
      this.props.availableVREntryTypes &&
      !preload &&
      (this.isWaitingForAutoExit() ? (
        <AutoExitWarningModal
          reason={this.state.autoExitReason}
          secondsRemaining={this.state.secondsRemainingBeforeAutoExit}
          onCancel={this.endAutoExitTimer}
        />
      ) : (
        <>
          <StateRoute stateKey="entry_step" stateValue="device" history={this.props.history}>
            {this.renderDevicePanel()}
          </StateRoute>
          <StateRoute stateKey="entry_step" stateValue="audio" history={this.props.history}>
            {this.renderAudioSetupPanel()}
          </StateRoute>
          <StateRoute
            stateKey="entry_step"
            stateValue="profile"
            history={this.props.history}
            render={props => (
              <ProfileEntryPanel
                {...props}
                containerType="modal"
                mediaSource={mediaSource}
                displayNameOverride={displayNameOverride}
                finished={() => {
                  const showAudioStep = shouldShowEntryAudioSetup(configManager.config);
                  if (this.props.forcedVREntryType) {
                    this.handleForceEntry();
                  } else if (showAudioStep) {
                    this.onRequestMicPermission();
                    this.pushHistoryState("entry_step", "audio");
                  } else {
                    this.onAudioReadyButton();
                  }
                }}
                showBackButton
                onBack={() => this.pushHistoryState()}
                store={this.props.store}
                onChangeAvatar={e => {
                  e.preventDefault();
                  this.setSidebar("avatar");
                }}
                avatarId={props.location.state.detail && props.location.state.detail.avatarId}
                enterInAR={this.state.enterInAR}
                dashboardChannel={this.props.dashboardChannel}
              />
            )}
          />
          <StateRoute stateKey="entry_step" stateValue="" history={this.props.history}>
            {this.renderEntryStartPanel()}
          </StateRoute>
        </>
      ));

    const presenceLogEntries = this.props.presenceLogEntries || [];

    const mediaSource = this.props.mediaSearchStore.getUrlMediaSource(this.props.history.location);

    // Allow scene picker pre-entry, otherwise wait until entry
    const showMediaBrowser =
      mediaSource && (["scenes", "avatars", "favorites"].includes(mediaSource) || this.state.entered);

    const streaming = this.state.isStreaming;

    const isLockedDownDemo = configs.feature("is_locked_down_demo_room") === this.props.hub.hub_id;

    const showObjectList = enteredOrWatching && !isLockedDownDemo;
    const showECSObjectsMenuButton = qsTruthy("ecsDebug");

    const streamer = getCurrentStreamer();
    const streamerName = streamer && streamer.displayName;

    const renderEntryFlow = Boolean((!enteredOrWatching && this.props.hub) || this.isWaitingForAutoExit());

    const destinationUrl = () => {
      return configs.destinationUrl(this.props.hub, this.props.embed);
    };

    if (!configManager.config.UI.enabled && !renderEntryFlow) {
      return (
        <StateContextProvider
          entered={this.state.entered}
          watching={this.state.watching}
          onEnteredChange={value => this.setState({ entered: value })}
          onWatchingChange={value => this.setState({ watching: value, ...(!value && { sidebarId: null }) })}
        >
          <div className={classNames(rootStyles)}>
            <RoomLayoutContainer
              context={this}
              hub={this.props.hub}
              history={this.props.history}
              mySessionId={this.props.sessionId}
              presences={this.props.presences}
              hubChannel={this.props.hubChannel}
              isNotAbsolute={isNotAbsolute}
              store={this.props.store}
              scene={this.props.scene}
              showToolbar={false}
              viewport={<></>}
            />
          </div>
        </StateContextProvider>
      );
    }

    return (
      <StateContextProvider
        entered={this.state.entered}
        watching={this.state.watching}
        onEnteredChange={value => this.setState({ entered: value })}
        onWatchingChange={value => this.setState({ watching: value, ...(!value && { sidebarId: null }) })}
      >
        <WorkspaceProvider>
          <AvatarPersistenceManager store={this.props.store} controller={this.props.avatarSelectionController} />
          <UploadFormProvider scene={this.props.scene}>
            <ModalContextProvider
              showNonHistoriedDialog={this.showNonHistoriedDialog}
              destinationUrl={destinationUrl}
              closeDialog={this.closeDialog}
            >
              <SidebarContextProvider sidebarId={this.state.sidebarId} setSidebar={this.setSidebar}>
                <EventContextProvider
                  slug={this.props.hub?.user_data?.event?.eventSlug || this.props.hub?.user_data?.event_slug}
                  sessionSlug={
                    this.props.hub?.user_data?.event?.roomRole === "session"
                      ? this.props.hub?.user_data?.event?.sessionSlug
                      : undefined
                  }
                  roomRole={this.props.hub?.user_data?.event?.roomRole || "lobby"}
                  performConditionalSignIn={this.props.performConditionalSignIn}
                  store={this.props.store}
                >
                <EventReadinessWrapper
                  onEventReady={this.onEventReady}
                  eventSlug={this.props.hub?.user_data?.event?.eventSlug || this.props.hub?.user_data?.event_slug}
                >
                  <ProfileMenuContextProvider>
                    <ReactAudioContext.Provider value={this.state.audioContext}>
                      <SettingsProvider
                        showVerseSettings={
                          this.props.hubChannel.canOrWillIfCreator("update_hub") &&
                          this.state.signedIn &&
                          configManager.config.roomEntryDialog?.roomSettings?.enabled
                        }
                      >
                        <MoreMenuContextProvider>
                          <div className={classNames(rootStyles)}>
                            {preload && this.props.hub && (
                              <PreloadOverlay
                                hubName={this.props.hub.name}
                                hubScene={this.props.hub.scene}
                                baseUrl={hubUrl(this.props.hub.hub_id).href}
                                onLoadClicked={this.props.onPreloadLoadClicked}
                              />
                            )}
                            {!this.state.dialog && (
                              <AvatarEditor
                                className={styles.avatarEditor}
                                signedIn={this.state.signedIn}
                                onSignIn={this.showContextualSignInDialog}
                                onSave={entry => {
                                  entry.id = entry.avatar_id;
                                  this.props.onAvatarSaved(entry.avatar_id);
                                  this.props.onMediaSearchResultEntrySelected(entry, "use");
                                }}
                                onClose={() => {
                                  this.props.history.goBack();
                                  // clearHistoryState(this.props.history);
                                  // setTimeout(() => {
                                  //   clearHistoryState(window.APP.mediaSearchStore.history);
                                  // }, 300)
                                }}
                                store={this.props.store}
                                debug={avatarEditorDebug}
                              />
                            )}
                            {!this.state.dialog && this.state.sidebarId === "avatar" && (
                              <FluentAvatarEditor
                                onClose={() => this.setSidebar(null)}
                                onAvatarSelected={entry => {
                                  requestAvatarSelection(this.props.scene, entry.id, { forceReload: true });
                                }}
                                avatarId={this.props.store.state.profile.avatarId}
                                performConditionalSignIn={this.props.performConditionalSignIn}
                                initialAvatarId={this.props.store.state.profile.avatarId}
                                showNonHistoriedDialog={this.showNonHistoriedDialog}
                                isSignedIn={this.state.signedIn}
                                store={this.props.store}
                                onSpawnAvatar={
                                  this.state.signedIn &&
                                  this.state.entered &&
                                  !!configManager.config.spawnAndMoveMedia.enabled &&
                                  this.props.hubChannel.can("spawn_and_move_media")
                                    ? entry => {
                                        this.props.onMediaSearchResultEntrySelected(entry, "spawn");
                                      }
                                    : undefined
                                }
                              />
                            )}
                            {this.props.hub && (
                              <RoomLayoutContainer
                                entered={this.state.entered}
                                hub={this.props.hub}
                                history={this.props.history}
                                mySessionId={this.props.sessionId}
                                presences={this.props.presences}
                                hubChannel={this.props.hubChannel}
                                dialogConnected={Boolean(this.props.dialogConnected)}
                                hasDialogConnectedOnce={Boolean(this.props.hasDialogConnectedOnce)}
                                renderEntryFlow={renderEntryFlow}
                                isNotAbsolute={isNotAbsolute}
                                scene={this.props.scene}
                                store={this.props.store}
                                objectFocused={!!this.props.selectedObject}
                                streaming={streaming}
                                sidebarLeft={["profile"].includes(this.state.sidebarId)}
                                toggleStreamerMode={this.toggleStreamerMode}
                                sidebarClassName={
                                  ["room-settings", "room-info-settings"].includes(this.state.sidebarId)
                                    ? "room-settings-sidebar"
                                    : ""
                                }
                                signedIn={this.state.signedIn}
                                performConditionalSignIn={this.props.performConditionalSignIn}
                                viewport={
                                  <>
                                    {entered && !this.props.selectedObject && (
                                      <div className={styles.avatarPreview}>
                                        <FirstPersonAvatarPreview
                                          onClick={() => this.setSidebar(null)}
                                          signedIn={isDashboardSignedIn}
                                          isAbsolute={true}
                                          profile={this.props.store.state.profile}
                                          email={maskEmail(this.props.store.state.credentials.email)}
                                          showContextualSignInDialog={this.showContextualSignInDialog}
                                          onEditAvatar={() => {
                                            this.setSidebar("avatar");
                                          }}
                                          onSignOut={this.signOut}
                                          store={this.props.store}
                                          dashboardChannel={this.props.dashboardChannel}
                                        ></FirstPersonAvatarPreview>
                                      </div>
                                    )}

                                    {!this.state.dialog && renderEntryFlow
                                      ? entryDialog
                                      : undefined}
                                    {/* {!this.props.selectedObject && <CompactMoreMenuButton />} */}
                                    {!entered && !streaming && !isMobile && streamerName && (
                                      <SpectatingLabel name={streamerName} />
                                    )}
                                    {this.props.activeObject && this.props.activeObject.is2D && (
                                      <Component2DView
                                        object={this.props.activeObject}
                                        sidebar={this.state.sidebarId}
                                        scene={this.props.scene}
                                        isMobile={isMobile}
                                        breakpoint={this.props.breakpoint}
                                      ></Component2DView>
                                    )}
                                    {this.props.focusedObject && this.props.selectedObject && (
                                      <ObjectMenuContainer
                                        hubChannel={this.props.hubChannel}
                                        scene={this.props.scene}
                                        dashboardChannel={this.props.dashboardChannel}
                                        enterSceneInVR={this.props.enterSceneInVR}
                                        onDeviceLinkHandoffConfirmed={this.handleDeviceLinkHandoffConfirmed}
                                        onOpenProfile={() => this.setSidebar("profile")}
                                        onGoToObject={() => {
                                          if (this.props.breakpoint === "sm") {
                                            this.setSidebar(null);
                                          }
                                        }}
                                      />
                                    )}
                                    {this.state.sidebarId !== "chat" && this.props.hub && (
                                      <PresenceLog
                                        mySessionId={this.props.sessionId}
                                        preset={"InRoom"}
                                        exclude={isSmallScreen ? [] : ["permission"]}
                                        presences={this.props.presences}
                                        entries={presenceLogEntries}
                                        hubId={this.props.hub.hub_id}
                                        history={this.props.history}
                                        onViewProfile={sessionId =>
                                          this.setSidebar("user", { selectedUserId: sessionId })
                                        }
                                      />
                                    )}
                                    <NotificationsContainer>
                                      {configManager.config.UI.enabled && (
                                        <TipContainer
                                          hide={this.props.activeObject}
                                          inRoom={entered}
                                          isEmbedded={this.props.embed}
                                          hubId={this.props.hub.hub_id}
                                          presences={this.props.presences}
                                          scene={this.props.scene}
                                          store={this.props.store}
                                        />
                                      )}

                                      {!isSmallScreen && (
                                        <PresenceLog
                                          mySessionId={this.props.sessionId}
                                          preset={"Notifications"}
                                          include={["permission"]}
                                          presences={this.props.presences}
                                          entries={presenceLogEntries}
                                          hubId={this.props.hub.hub_id}
                                          history={this.props.history}
                                          onViewProfile={sessionId =>
                                            this.setSidebar("user", { selectedUserId: sessionId })
                                          }
                                        />
                                      )}
                                    </NotificationsContainer>
                                    {(showRtcDebugPanel || showAudioDebugPanel) && (
                                      <RTCDebugPanel
                                        history={this.props.history}
                                        store={window.APP.store}
                                        scene={this.props.scene}
                                        presences={this.props.presences}
                                        sessionId={this.props.sessionId}
                                        showRtcDebug={showRtcDebugPanel}
                                        showAudioDebug={showAudioDebugPanel}
                                      />
                                    )}
                                    {this.state.sidebarId === "ecs-debug" && (
                                      <ECSDebugSidebarContainer onClose={() => this.setSidebar(null)} />
                                    )}
                                    {this.state.toast.dialog && (
                                      <ToastDialog
                                        Component={this.state.toast.Component}
                                        {...this.state.toast.toastProps}
                                      />
                                    )}

                                    <FloatyButtonsContainer>
                                      <>
                                        {configManager.config.showButtonInfo.enabled &&
                                          configManager.config.UI.enabled &&
                                          !this.props.selectedObject && (
                                            <ButtonInfo
                                              room={this.props.hub}
                                              dashboardChannel={this.props.dashboardChannel}
                                              open={this.state.sidebarId === "invite"}
                                              onOpenChange={open => this.setSidebar(open ? "invite" : null)}
                                              entered={this.state.entered}
                                            />
                                          )}
                                        {configManager.config.UI.enabled &&
                                          configManager.config.roomEntryDialog?.enterOnVR.enabled &&
                                          this.state.entered && (
                                            <EnterOnVRPopover
                                              scene={this.props.scene}
                                              open={this.state.showEnterOnDevicePopover}
                                              onOpen={() => {
                                                if (this.props.forcedVREntryType?.startsWith("vr")) {
                                                  this.props.enterSceneInVR();
                                                } else {
                                                  this.attemptLink();
                                                  this.setState({ showEnterOnDevicePopover: true });
                                                }
                                              }}
                                              onClose={() => {
                                                this.cancelActiveDeviceLink();

                                                this.setState({
                                                  showEnterOnDevicePopover: false
                                                });
                                              }}
                                            >
                                              <EnterOnDevice
                                                shortUrl={getGateLinkUrl()}
                                                loadingCode={this.state.loadingLinkCode}
                                                code={this.state.linkCode}
                                                headsetConnected={AFRAME.utils.device.checkHeadsetConnected()}
                                                unsupportedBrowser={
                                                  this.props.availableVREntryTypes.generic ===
                                                  VR_DEVICE_AVAILABILITY.maybe
                                                }
                                                onEnterOnConnectedHeadset={async () => {
                                                  this.props.enterSceneInVR();
                                                  this.cancelActiveDeviceLink();
                                                  this.setState({
                                                    showEnterOnDevicePopover: false
                                                  });
                                                }}
                                                attemptLink={this.attemptLink}
                                              />
                                            </EnterOnVRPopover>
                                          )}
                                        {this.state.entered &&
                                          this.props.hub?.user_data?.portals?.length > 0 &&
                                          !this.props.selectedObject && <PortalsButton />}
                                      </>
                                    </FloatyButtonsContainer>

                                    <RoomSettingsSidebarContainer
                                      store={this.props.store}
                                      scene={this.props.scene}
                                      hubChannel={this.props.hubChannel}
                                      hub={this.props.hub}
                                      signedIn={this.state.signedIn}
                                      dashboardChannel={this.props.dashboardChannel}
                                      roomSession={this.state.roomSession}
                                    />
                                    {this.state.sidebarId === "assets" && (
                                      <AssetsSidebarContainer
                                        scene={this.props.scene}
                                        signedIn={this.state.signedIn}
                                        userName={this.props.store.state.profile.displayName ?? "Someone"}
                                        sessionId={this.props.sessionId}
                                      />
                                    )}
                                    {(this.state.sidebarId === "assets.public-domain-art" ||
                                      this.state.sidebarId === "assets.libraries" ||
                                      this.state.sidebarId === "assets.sketchfab") && (
                                      <AssetsSidebarContainer
                                        signedIn={this.state.signedIn}
                                        initialTab={
                                          this.state.sidebarId === "assets.public-domain-art"
                                            ? "public-domain-art"
                                            : "libraries"
                                        }
                                        scene={this.props.scene}
                                      />
                                    )}
                                    {this.state.sidebarId === "object" && (
                                      <ObjectInspectSidebarContainer
                                        hubChannel={this.props.hubChannel}
                                        scene={this.props.scene}
                                      />
                                    )}
                                    <DropArea
                                      scene={this.props.scene}
                                      userName={this.props.store.state.profile.displayName ?? "Someone"}
                                      sessionId={this.props.sessionId}
                                    />
                                    {this.state.sidebarId === "artwork-info" && (
                                      <ArtworkInfoSidebarContainer
                                        hubChannel={this.props.hubChannel}
                                        scene={this.props.scene}
                                      />
                                    )}
                                  </>
                                }
                                sidebar={
                                  this.state.sidebarId ? (
                                    <>
                                      {this.state.sidebarId === "schedule" && (
                                        <ScheduleSidebar onClose={() => this.setSidebar(null)} />
                                      )}
                                      {this.state.sidebarId === "portals" && this.props.hub?.user_data?.portals && (
                                        <PortalsSidebarContainer
                                          onClose={() => this.setSidebar(null)}
                                          portals={this.props.hub?.user_data.portals}
                                        />
                                      )}
                                      {this.state.sidebarId === "objects" &&
                                        configManager.config.canInspectObjects.enabled && (
                                          <ObjectsSidebarContainer
                                            hubChannel={this.props.hubChannel}
                                            onClose={() => this.setSidebar(null)}
                                          />
                                        )}
                                      {this.state.sidebarId === "profile" && (
                                        <ProfileEntryPanel
                                          history={this.props.history}
                                          containerType="sidebar"
                                          displayNameOverride={displayNameOverride}
                                          finished={() => this.setSidebar(null)}
                                          onClose={() => this.setSidebar(null)}
                                          store={this.props.store}
                                          onChangeAvatar={e => {
                                            e.preventDefault();
                                            this.setSidebar("avatar");
                                          }}
                                          mediaBrowserOpened={showMediaBrowser}
                                          dashboardChannel={this.props.dashboardChannel}
                                        />
                                      )}
                                      {this.state.sidebarId === "user" && (
                                        <UserProfileSidebarContainer
                                          user={this.getSelectedUser()}
                                          hubChannel={this.props.hubChannel}
                                          performConditionalSignIn={this.props.performConditionalSignIn}
                                          onClose={() => this.setSidebar(null)}
                                          onCloseDialog={() => this.closeDialog()}
                                          showNonHistoriedDialog={this.showNonHistoriedDialog}
                                        />
                                      )}
                                      {this.state.sidebarId === "room-info" && (
                                        <RoomSidebar
                                          accountId={this.props.sessionId}
                                          room={this.props.hub}
                                          canEdit={this.props.hubChannel.canOrWillIfCreator("update_hub")}
                                          onEdit={() => {
                                            this.props.performConditionalSignIn(
                                              () => this.props.hubChannel.can("update_hub"),
                                              () => this.setSidebar("room-info-settings"),
                                              SignInMessages.roomSettings
                                            );
                                          }}
                                          onClose={() => this.setSidebar(null)}
                                          onChangeScene={this.onChangeScene}
                                        />
                                      )}
                                    </>
                                  ) : undefined
                                }
                                modal={this.state.dialog}
                                toolbarLeft={
                                  <InvitePopoverContainer
                                    hub={this.props.hub}
                                    hubChannel={this.props.hubChannel}
                                    dashboardChannel={this.props.dashboardChannel}
                                    scene={this.props.scene}
                                    store={this.props.store}
                                  />
                                }
                                showToolbar={this.state.showToolbar}
                                mediaSearchStore={this.props.mediaSearchStore}
                              />
                            )}
                            <IframeLinkModal
                              open={this.state.iframe2D.dialog}
                              onClose={this.closeiframe2D}
                              src={this.state.iframe2D.src}
                            ></IframeLinkModal>
                          </div>
                        </MoreMenuContextProvider>
                      </SettingsProvider>
                    </ReactAudioContext.Provider>
                  </ProfileMenuContextProvider>
                </EventReadinessWrapper>
              </EventContextProvider>
            </SidebarContextProvider>
          </ModalContextProvider>
        </UploadFormProvider>
      </WorkspaceProvider>
      </StateContextProvider>
    );
  }
}

function UIRootHooksWrapper(props) {
  return (
    <RoomAssetLoadingProvider scene={props.scene}>
      <UIRootHooksWrapperContent {...props} />
    </RoomAssetLoadingProvider>
  );
}

function UIRootHooksWrapperContent(props) {
  useAccessibleOutlineStyle();
  const breakpoint = useCssBreakpoints();
  const { voice_chat: canVoiceChat } = usePermissions();
  const isAuthenticated = Boolean(props.store.state.credentials?.token);
  const isLoading = false;
  const error = null;
  const [sceneLoading, setSceneLoading] = useState(null);
  const [portaling, setPortaling] = useState(false);
  const [uploadForm, setUploadForm] = useState({ dialog: false });
  const [isUIRootShowingLoadingScreen, setIsUIRootShowingLoadingScreen] = useState(false); // New state
  const { assetsLoading } = useRoomAssetLoading();

  const handleUIRootLoadingStateChange = useCallback(isShowing => {
    setIsUIRootShowingLoadingScreen(isShowing);
  }, []);

  const isAnyLoadingScreenActive = sceneLoading || assetsLoading || isUIRootShowingLoadingScreen;
  const isLoadingOverlayVisible = sceneLoading || assetsLoading;

  useEffect(() => {
    const el = document.getElementById("preload-overlay");
    el.classList.add("loaded");

    const sceneEl = props.scene;

    sceneEl.classList.add(roomLayoutStyles.scene);

    // Remove the preload overlay after the animation has finished.
    const timeout = setTimeout(() => {
      el.remove();
    }, 500);

    return () => {
      clearTimeout(timeout);
      sceneEl.classList.remove(roomLayoutStyles.scene);
    };
  }, [props.scene]);

  const showUploadForm = () => {
    setUploadForm({ dialog: true });
  };

  const closeUploadForm = () => {
    setUploadForm({ dialog: false });
  };

  // On mounted listen for the scene loading event
  useEffect(() => {
    const handleSceneLoading = ({ detail }) => {
      setSceneLoading(detail ? detail.name : true);
    };

    const handleSceneLoaded = () => {
      setSceneLoading(null);
    };

    const handlePortalingInitiated = () => {
      setPortaling(true);
    };

    const handlePortalingComplete = () => {
      setPortaling(false);
    };

    props.scene.addEventListener("scene-loading", handleSceneLoading);
    props.scene.addEventListener("scene-loaded", handleSceneLoaded);
    props.scene.addEventListener("portaling-initiated", handlePortalingInitiated);
    props.scene.addEventListener("portaling-complete", handlePortalingComplete);

    window.addEventListener("open-upload-dropzone-form", showUploadForm);

    return () => {
      props.scene.removeEventListener("scene-loading", handleSceneLoading);
      props.scene.removeEventListener("scene-loaded", handleSceneLoaded);
      props.scene.removeEventListener("portaling-initiated", handlePortalingInitiated);
      props.scene.removeEventListener("portaling-complete", handlePortalingComplete);
      window.removeEventListener("open-upload-dropzone-form", showUploadForm);
    };
  }, [props.scene]);

  useEffect(() => {
    if (props.sessionId) {
      window.mySessionId = props.sessionId;
    }
  }, [props.sessionId]);

  // Wait for the isLoading to be false and check if the user is authenticated on useeffect

  return (
    <DndProvider backend={HTML5Backend}>
      <RoomPersistenceProvider
        dashboardApiUrl={configs.CUSTOM_API_URL}
        hub={props.hub}
        hubChannel={props.hubChannel}
        mySessionId={props.sessionId}
        nestApiUrl={configs.CUSTOM_API_URL}
        store={props.store}
      >
        <ChatContextProvider messageDispatch={props.messageDispatch} mySessionId={props.sessionId}>
          <PrivateChatContextProvider messageDispatch={props.messageDispatch} mySessionId={props.sessionId}>
            <ObjectSettingsProvider hubChannel={props.hubChannel} scene={props.scene}>
              <CameraDevicesProvider>
                <ObjectListProvider scene={props.scene}>
                  <LoadingDots isLoading={portaling} />
                  {isLoadingOverlayVisible ? (
                    <LoadingScreenContainer
                      scene={props.scene}
                      hub={props.hub}
                      onLoaded={() => false}
                      destination={sceneLoading === true ? null : sceneLoading}
                    />
                  ) : (
                    <></>
                  )}
                  <UIRoot
                    {...props}
                    breakpoint={breakpoint}
                    canVoiceChat={canVoiceChat && configManager.config.voiceChat.enabled && !isWebRtcDisabled()}
                    isLoading={isLoading}
                    error={error}
                    isAuthenticated={isAuthenticated}
                    onInternalLoadingStateChange={handleUIRootLoadingStateChange}
                  />
                  <ObjectUrlUploadModalContainer
                    open={uploadForm.dialog}
                    onClose={closeUploadForm}
                    scene={props.scene}
                  ></ObjectUrlUploadModalContainer>
                  <NotificationsCenterToaster loading={isAnyLoadingScreenActive} />
                  <CookieConsentModalContainer />
                  <Video2DContainer />
                </ObjectListProvider>
              </CameraDevicesProvider>
            </ObjectSettingsProvider>
          </PrivateChatContextProvider>
        </ChatContextProvider>
      </RoomPersistenceProvider>
    </DndProvider>
  );
}

UIRootHooksWrapperContent.propTypes = {
  scene: PropTypes.object.isRequired,
  messageDispatch: PropTypes.object,
  store: PropTypes.object.isRequired,
  sessionId: PropTypes.string,
  connectToDialog: PropTypes.func,
  hubChannel: PropTypes.object,
  hub: PropTypes.object
};

UIRootHooksWrapper.propTypes = UIRootHooksWrapperContent.propTypes;

export default UIRootHooksWrapper;
